import * as Sentry from "@sentry/react";
import { useEffect, useRef } from "react";
import { logToSentry } from "utils/logger";

type Params = {
	isSubscriptionBroken: boolean;
	fridgeId: string;
};

/**
 * Symmetric counterpart to the broken-subscription logs. The broken flag is raised by several lanes
 * (retry-link error, liveness watchdog, onComplete/onError termination, the offline event) and cleared
 * silently by either the first delivery/registration frame (useClearBrokenOnInbound) or the first data frame
 * (DeviceContext.onData). Watching the broken→recovered falling edge is the single point that observes
 * recovery regardless of which lane cleared it, so it emits exactly one "restored" event per outage —
 * pairing each broken event with its recovery in Sentry. The rising edge stamps when the stream went down
 * so the restore can report how long it was out. Passive: it only reacts to the state transition.
 */
export const useLogSubscriptionRestored = ({ isSubscriptionBroken, fridgeId }: Params): void => {
	const wasBrokenRef = useRef(isSubscriptionBroken);
	const brokenSinceRef = useRef<number | null>(null);

	useEffect(() => {
		const wasBroken = wasBrokenRef.current;
		wasBrokenRef.current = isSubscriptionBroken;

		if (!wasBroken && isSubscriptionBroken) {
			brokenSinceRef.current = Date.now();
			return;
		}

		if (wasBroken && !isSubscriptionBroken) {
			const downtimeMs = brokenSinceRef.current == null ? null : Date.now() - brokenSinceRef.current;
			brokenSinceRef.current = null;
			Sentry.withScope((scope) => {
				if (downtimeMs != null) {
					scope.setExtra("downtimeMs", downtimeMs);
					scope.setTag("downtimeSeconds", String(Math.round(downtimeMs / 1000)));
				}
				logToSentry(`Subscription restored, fridge: ${fridgeId}`, "info");
			});
		}
	}, [isSubscriptionBroken, fridgeId]);
};
